import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles, Loader2 } from 'lucide-react';
import { useStacks } from '@/lib/hooks';
import { getGreeting } from '@/lib/store';
import { StackCard } from '@/components/StackCard';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function TodayPage() {
    const navigate = useNavigate();
    const { activeStacks, getStackProgress, isLoading } = useStacks();

    const greeting = getGreeting();

    const stacksWithProgress = useMemo(() => {
        return activeStacks
            .map(stack => ({
                stack,
                progress: getStackProgress(stack.id),
            }))
            .sort((a, b) => {
                if (a.progress.isComplete && !b.progress.isComplete) return 1;
                if (!a.progress.isComplete && b.progress.isComplete) return -1;
                return a.stack.position - b.stack.position;
            });
    }, [activeStacks, getStackProgress]);

    const incompleteCount = stacksWithProgress.filter(s => !s.progress.isComplete).length;
    const allComplete = stacksWithProgress.length > 0 && incompleteCount === 0;

    if (isLoading) {
        return (
            <div className="max-w-lg mx-auto px-4 py-6">
                <header className="mb-6">
                    <p className="text-sm text-muted-foreground mb-1">{greeting}</p>
                    <div className="h-8 w-48 bg-muted rounded animate-pulse" />
                </header>
                <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 py-6">
            {/* Header */}
            <header className="mb-6">
                <p className="text-sm text-muted-foreground mb-1">{greeting}</p>
                <h1 className="text-2xl font-bold text-foreground">
                    {stacksWithProgress.length === 0
                        ? "Let's get started"
                        : allComplete
                            ? "All done for today"
                            : `${incompleteCount} ${incompleteCount === 1 ? 'stack' : 'stacks'} to go`
                    }
                </h1>
            </header>

            {/* All complete celebration */}
            {allComplete && (
                <div className="mb-6 p-5 rounded-lg bg-success-light border border-success/20 animate-scale-in">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-success" />
                        </div>
                        <div>
                            <p className="font-semibold text-foreground">You showed up today.</p>
                            <p className="text-sm text-muted-foreground">That's what matters.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Stacks list */}
            {stacksWithProgress.length > 0 ? (
                <div className="space-y-3">
                    {stacksWithProgress.map(({ stack, progress }, index) => (
                        <div
                            key={stack.id}
                            className="animate-slide-up"
                            style={{ animationDelay: `${index * 0.05}s` }}
                        >
                            <StackCard stack={stack} progress={progress} />
                        </div>
                    ))}
                </div>
            ) : (
                <EmptyState
                    icon={Plus}
                    title="No stacks yet"
                    description="Create your first stack. Keep it small — a few habits that flow together."
                    actionLabel="Create Stack"
                    onAction={() => navigate('/stacks/new')}
                />
            )}

            {/* FAB */}
            {stacksWithProgress.length > 0 && (
                <Button
                    onClick={() => navigate('/stacks/new')}
                    size="icon"
                    className="fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-elevated z-40 hover:scale-105 transition-transform"
                >
                    <Plus className="w-6 h-6" />
                </Button>
            )}
        </div>
    );
}
